import {concatenate} from "../../../services/string";
import React from "react";
import EventItem from "./EventItem";
import {DateTime, Interval} from "luxon";
import Spinner from "../../../components/Spinner";
import {Event} from 'types/Event'
import {CalendarView} from "./EventSchedule";
import {FIVE_MINUTE_SLOTS, HALF_HOUR_SLOTS} from "./DayView";

function isToday(date: DateTime): boolean {
  return DateTime.now().startOf('day').equals(date.startOf('day'))
}

function hoursOfDay(): DateTime[] {
  const time = DateTime.now().startOf('day')
  const hours = []

  for (let i = 0; i < 24; i++) {
    hours.push(time.plus({hours: i}))
  }

  return hours
}

type WeekViewProps = {
  selectedDate: DateTime
  selectDate: (date: DateTime) => void
  dates: Array<Interval>
  events: Array<Event> | undefined
  isLoading: boolean
  onDeleteEvent: (event: Event) => void
  calendarView: CalendarView
}

function WeekView({selectedDate, selectDate, dates, events, isLoading, onDeleteEvent, calendarView}: WeekViewProps) {
  const eventColumns = [
    'sm:col-start-1',
    'sm:col-start-2',
    'sm:col-start-3',
    'sm:col-start-4',
    'sm:col-start-5',
    'sm:col-start-6',
    'sm:col-start-7',
  ]

  return (
    <div
      className="relative flex flex-auto flex-col overflow-auto bg-white shadow scrollbar-thin scrollbar-track-rounded-lg scrollbar-track-gray-200 scrollbar-thumb-rounded-lg scrollbar-thumb-gray-400 h-[calc(100vh_-_16rem)] md:h-[calc(100vh_-_14rem)]">
      <div className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full relative">
        {/* Day Headers */}
        <div className="sticky top-0 z-40 flex-none bg-white shadow ring-1 ring-black ring-opacity-5 sm:pr-8">

          {/* Mobile Day Headers */}
          <div className="grid grid-cols-7 text-sm leading-6 text-gray-500 sm:hidden">
            {dates.map(date => (
              <button
                type="button"
                className="flex flex-col items-center pt-2 pb-3"
                key={date.start.day}
                onClick={() => selectDate(date.start)}
              >
                {date.start.weekdayShort.substring(0, 1)}{' '}
                <span className={concatenate(
                  isToday(date.start) ? 'bg-brand hover:bg-brand text-white rounded-full' : (date.start.equals(selectedDate) ? ' bg-gray-300 hover:bg-gray-200 text-gray-900 rounded-full' : 'text-gray-900'),
                  'mt-1 flex h-8 w-8 items-center justify-center font-semibold')}
                >
                    {date.start.day}
                </span>
              </button>
            ))}
          </div>

          {/* Desktop Day Headers */}
          <div
            className="-mr-px hidden grid-cols-7 divide-x divide-gray-100 border-r border-gray-100 text-sm leading-6 text-gray-500 sm:grid">
            <div className="col-end-1 w-14 flex items-center justify-center">
              {/* Loading spinner */}
              {isLoading &&
                <Spinner color={Spinner.colors.DARK} size={Spinner.sizes.X_SMALL}/>
              }
            </div>

            {dates.map(date => (
              <div className="flex items-center justify-center py-3" key={date.start.day}>
                <span className='flex items-baseline'>
                    {date.start.weekdayShort}{' '}
                  <span
                    className={concatenate(isToday(date.start) ? 'bg-brand text-white ml-1.5' : 'text-gray-900', 'rounded-full flex h-8 w-8 items-center justify-center font-semibold')}>
                        {date.start.day}
                    </span>
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-auto">
          <div className="sticky left-0 w-14 flex-none bg-white ring-1 ring-gray-100"/>
          <div className="grid flex-auto grid-cols-1 grid-rows-1">
            {/* Horizontal lines */}
            <div
              className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100"
              style={{gridTemplateRows: `repeat(${HALF_HOUR_SLOTS}, minmax(3.5rem, 1fr))`}}>

              {/* Current time indicator */}
              <div className="row-end-1 h-7"/>

              {/* Hours of day */}
              {hoursOfDay().map(time => (
                <React.Fragment key={time.hour}>
                  <div>
                    <div
                      className="sticky left-0 -mt-2.5 -ml-14 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                      {time.toFormat('ha')}
                    </div>
                  </div>
                  <div/>
                </React.Fragment>
              ))}
            </div>

            {/* Vertical lines */}
            <div
              className="col-start-1 col-end-2 row-start-1 hidden grid-cols-7 grid-rows-1 divide-x divide-gray-100 sm:grid sm:grid-cols-7">
              <div className="col-start-1 row-span-full"/>
              <div className="col-start-2 row-span-full"/>
              <div className="col-start-3 row-span-full"/>
              <div className="col-start-4 row-span-full"/>
              <div className="col-start-5 row-span-full"/>
              <div className="col-start-6 row-span-full"/>
              <div className="col-start-7 row-span-full"/>
              <div className="col-start-8 row-span-full w-8"/>
            </div>

            {/* Events */}
            <ol
              className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:grid-cols-7 sm:pr-8 auto-cols-fr"
              style={{gridTemplateRows: `1.75rem repeat(${FIVE_MINUTE_SLOTS}, minmax(0, 1fr)) auto`}}
            >
              {events?.map(event => (
                <EventItem
                  event={event}
                  eventList={events}
                  selectedDate={selectedDate}
                  calendarView={calendarView}
                  classNames={eventColumns[event.starts_at.weekday - 1]}
                  onDeleteEvent={onDeleteEvent}
                  key={event.id}
                />
              ))}
            </ol>
          </div>
        </div>

        {/* Loading overlay */}
        {isLoading && <div className='absolute bg-white inset-0 flex items-center justify-center opacity-50'/>}
      </div>
    </div>
  )
}

export default WeekView